import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { Can, useCan } from '../../lib/rbac';

interface InternalPending {
  id: number;
  ml_order_id: number;
  asin: string | null;
  internal_sku: string | null;
  quantity: number;
  created_at: string;
  buyer_nickname: string | null;
  buyer_first_name: string | null;
  buyer_last_name: string | null;
  title: string | null;
  image_url: string | null;
}

function ageColor(createdAt: string): string {
  const ageHours = (Date.now() - new Date(createdAt).getTime()) / 3600000;
  if (ageHours > 48) return 'text-red-700 font-bold';
  if (ageHours > 24) return 'text-orange-600';
  return 'text-gray-700';
}

function ageText(createdAt: string): string {
  const ms = Date.now() - new Date(createdAt).getTime();
  const hours = Math.floor(ms / 3600000);
  if (hours < 1) return 'recién';
  if (hours < 24) return `hace ${hours}h`;
  return `hace ${Math.floor(hours / 24)}d`;
}

// El apodo se ve siempre; el nombre real solo con la llave de datos de compradores.
function buyerName(it: InternalPending, canBuyers: boolean): string {
  if (it.buyer_nickname) return it.buyer_nickname;
  if (!canBuyers) return '—';
  return `${it.buyer_first_name || ''} ${it.buyer_last_name || ''}`.trim() || '—';
}

export function InternalPendingBanner({
  onPick,
  sellerId,
}: {
  onPick: (result: any) => void;
  sellerId: number;
}) {
  const canBuyers = useCan()('global.view_buyers');
  const [items, setItems] = useState<InternalPending[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [pickingId, setPickingId] = useState<number | null>(null);

  const refresh = async () => {
    try {
      const r = await api.get<InternalPending[]>(
        `/warehouse/internal-pending?seller_id=${sellerId}`
      );
      if (r.success) setItems((r.data as any) || []);
    } catch {
      // Silent — keep last known list
    }
  };

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 30000);
    return () => clearInterval(iv);
  }, [sellerId]);

  const pick = async (id: number) => {
    setPickingId(id);
    try {
      const r = await api.post<any>('/warehouse/internal-pick', {
        internal_purchase_id: id,
      });
      if (r.success) {
        onPick(r.data);
        await refresh();
      } else {
        alert('Error al pickear: ' + (r.error || 'unknown'));
      }
    } catch (e: any) {
      alert('Error al pickear: ' + e.message);
    } finally {
      setPickingId(null);
    }
  };

  if (items.length === 0) return null;

  return (
    <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-4 rounded">
      <div className="flex justify-between items-center">
        <strong>
          ⚠ Tenés {items.length}{' '}
          {items.length === 1 ? 'orden pendiente' : 'órdenes pendientes'} de stock interno
        </strong>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-sm bg-white border px-3 py-1 rounded hover:bg-gray-50"
        >
          {expanded ? 'Ocultar' : 'Ver lista'}
        </button>
      </div>

      {expanded && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-orange-100">
              <tr>
                <th className="text-left p-2">ASIN</th>
                <th className="text-left p-2">Título</th>
                <th className="text-left p-2">SKU interno</th>
                <th className="text-left p-2">Buyer</th>
                <th className="text-left p-2">Antigüedad</th>
                <th className="text-left p-2">Acción</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b">
                  <td className="p-2 font-mono">{it.asin || '—'}</td>
                  <td className="p-2 max-w-xs truncate">{it.title || '—'}</td>
                  <td className="p-2 font-mono text-xs">{it.internal_sku || '—'}</td>
                  <td className="p-2">{buyerName(it, canBuyers)}</td>
                  <td className={`p-2 ${ageColor(it.created_at)}`}>{ageText(it.created_at)}</td>
                  <td className="p-2">
                    <Can cap="warehouse.operate">
                      <button
                        onClick={() => pick(it.id)}
                        disabled={pickingId === it.id}
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm disabled:bg-gray-300 hover:bg-blue-700"
                      >
                        {pickingId === it.id ? 'Pickeando...' : '🖨 Imprimir etiqueta'}
                      </button>
                    </Can>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
